import React, { useState, useEffect } from "react";
import {
  IconLogin,
  IconUser,
  IconLogout,
  IconTrash,
  IconSettings,
} from "@tabler/icons-react";
import { useNavigate, useLocation } from "react-router-dom";
import { checkAuth, logout, deleteAccount } from "../../api";
import styles from "./Header.module.css";
import { notifications } from "@mantine/notifications";
import {
  nonProtectedRoutes,
  protectedRoutes,
} from "../../utils/nonProtectedRoutingPaths";
import {
  Text,
  Container,
  Burger,
  Box,
  Button,
  Drawer,
  Stack,
  Menu,
  Skeleton,
} from "@mantine/core";

export const Header = ({ minimal = false }: { minimal?: boolean } = {}) => {
  const [opened, setOpened] = useState(false);
  const toggle = () => setOpened((o) => !o);

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [username, setUsername] = useState("");
  const [tenantName, setTenantName] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const checkLoginStatus = async () => {
      setIsAuthLoading(true);
      try {
        const response = await checkAuth();
        if (response.isValid) {
          setIsLoggedIn(true);
          setUsername(response.username);
          setTenantName(response.tenantName);
        } else {
          setIsLoggedIn(false);
          setUsername("");
          setTenantName("");
        }
      } catch (error) {
        console.error("Token verification failed:", error);
        setIsLoggedIn(false);
        setUsername("");
        setTenantName("");
      } finally {
        setIsAuthLoading(false);
      }
    };

    checkLoginStatus();
  }, [location, navigate]);

  const handleNavigation = (path) => {
    navigate(path);
    setOpened(false);
  };

  const handleLogoClick = () => {
    if (minimal) {
      const params = new URLSearchParams(window.location.search);
      const from = params.get("from");
      const referrer = from || document.referrer;
      if (referrer) {
        window.location.href = referrer;
        return;
      }
    }
    navigate(nonProtectedRoutes.HOME);
  };

  const handleLogout = async () => {
    await logout();
    window.location.reload();
    navigate(nonProtectedRoutes.HOME);
  };

  const handleProfileClick = () => {
    navigate("/app/profile");
  };

  const handleAdminClick = () => {
    navigate("/app/tenant");
  };

  const handleDeleteAccount = async () => {
    const confirmed = window.confirm(
      "Are you sure you want to delete your account? This action is irreversible!",
    );

    if (confirmed) {
      try {
        await deleteAccount();
        notifications.show({
          message: "Account deleted successfully",
          color: "green",
          autoClose: 3000,
        });
        navigate("/");
        window.location.reload();
      } catch (error) {
        notifications.show({
          message: "Failed to delete account",
          color: "red",
          autoClose: 3000,
        });
      }
    }
  };

  const renderNavLinks = (mobile = false) => {
    const Component = mobile ? Stack : "nav";
    const componentProps = mobile
      ? { spacing: "md" }
      : { className: styles.navLinks };

    return (
      <Component {...componentProps}>
        <Text
          component="span"
          onClick={() => handleNavigation(nonProtectedRoutes.HOME)}
          size="sm"
          style={{ cursor: "pointer" }}
        >
          Home
        </Text>
        {isLoggedIn && (
          <Text
            component="span"
            onClick={() => handleNavigation(protectedRoutes.DASHBOARD)}
            size="sm"
            style={{ cursor: "pointer" }}
          >
            Dashboard
          </Text>
        )}
        <Text
          component="span"
          onClick={() => handleNavigation(nonProtectedRoutes.PRICING)}
          size="sm"
          style={{ cursor: "pointer" }}
        >
          Pricing
        </Text>
        <Text
          component="span"
          onClick={() => handleNavigation(nonProtectedRoutes.CONTACT)}
          size="sm"
          style={{ cursor: "pointer" }}
        >
          Contact
        </Text>
      </Component>
    );
  };

  const renderMobileDrawer = () => {
    return (
      <Drawer
        opened={opened}
        onClose={() => setOpened(false)}
        title={isLoggedIn ? `Hello, ${username}` : "Menu"}
        padding="xl"
        position="right"
        size="xs"
      >
        <Stack>
          {renderNavLinks(true)}
          {isLoggedIn ? (
            <>
              <Text
                component="span"
                onClick={handleProfileClick}
                size="sm"
                style={{ cursor: "pointer" }}
              >
                Profile
              </Text>
              <Text
                component="span"
                onClick={handleAdminClick}
                size="sm"
                style={{ cursor: "pointer" }}
              >
                Admin
              </Text>
              <Button
                onClick={() => {
                  logout();
                  setOpened(false);
                  navigate(nonProtectedRoutes.HOME);
                }}
                color="red"
                variant="light"
              >
                Logout
              </Button>
            </>
          ) : (
            <Button
              onClick={() => {
                setOpened(false);
                window.location.href = "/login";
              }}
              color="var(--primary-color)"
            >
              <Box mr={10}>Login</Box>
              <IconLogin size={18} stroke={1.5} />
            </Button>
          )}
        </Stack>
      </Drawer>
    );
  };

  const renderLoggedOutHeader = () => {
    return (
      <header className={styles.header}>
        <Container size="xl" className={styles.headerContent} style={{ position: "relative" }}>
          <img
            src="/static/Socius_DRK.svg"
            alt="Socius Legal"
            className={styles.logo}
            onClick={handleLogoClick}
            style={minimal ? { cursor: "pointer", position: "absolute", left: "50%", transform: "translateX(-50%)" } : { cursor: "pointer" }}
          />

          {!minimal && <Box className={styles.desktopOnly}>{renderNavLinks()}</Box>}

          <div className={styles.authSection} style={minimal ? { marginLeft: "auto" } : undefined}>
            <Button
              onClick={() => (window.location.href = "/login")}
              className={`${styles.loginBtn} ${minimal ? "" : styles.desktopOnly}`}
              color="var(--primary-color)"
              size="xs"
            >
              <Box mr={10}>Login</Box>
              <IconLogin size={18} stroke={1.5} />
            </Button>
            {!minimal && (
              <Burger
                opened={opened}
                onClick={toggle}
                className={styles.mobileOnly}
              />
            )}
          </div>
        </Container>
      </header>
    );
  };

  const renderLoggedInHeader = () => {
    return (
      <header className={styles.header}>
        <Container size="xl" className={styles.headerContent} style={{ position: "relative" }}>
          <img
            src="/static/Socius_DRK.svg"
            alt="Socius Legal"
            className={styles.logo}
            onClick={handleLogoClick}
            style={minimal ? { cursor: "pointer", position: "absolute", left: "50%", transform: "translateX(-50%)" } : { cursor: "pointer" }}
          />
          {!minimal && <Box className={styles.desktopOnly}>{renderNavLinks()}</Box>}
          <div className={styles.authSection} style={minimal ? { marginLeft: "auto" } : undefined}>
            <Menu position="bottom-end" withArrow>
              <Menu.Target>
                <Text
                  size="sm"
                  c="dimmed"
                  className={minimal ? "" : styles.desktopOnly}
                  style={{ cursor: "pointer" }}
                >
                  {username}
                </Text>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  leftSection={<IconUser size={16} />}
                  onClick={handleProfileClick}
                >
                  My Profile
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconSettings size={16} />}
                  onClick={handleAdminClick}
                >
                  Admin dashboard
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconLogout size={16} />}
                  onClick={handleLogout}
                >
                  Logout
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconTrash size={16} />}
                  color="red"
                  onClick={handleDeleteAccount}
                >
                  Delete Account
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
            {!minimal && (
              <Burger
                opened={opened}
                onClick={toggle}
                className={styles.mobileOnly}
              />
            )}
          </div>
        </Container>
      </header>
    );
  };

  const renderAuthSection = () => {
    if (isAuthLoading) {
      return (
        <header className={styles.header}>
          <Container size="xl" className={styles.headerContent} style={{ position: "relative" }}>
            <img
              src="/static/Socius_DRK.svg"
              alt="Socius Legal"
              className={styles.logo}
              style={minimal ? { cursor: "pointer", position: "absolute", left: "50%", transform: "translateX(-50%)" } : { cursor: "pointer" }}
            />
            {!minimal && <Box className={styles.desktopOnly}>{renderNavLinks()}</Box>}
            <div className={styles.authSection} style={minimal ? { marginLeft: "auto" } : undefined}>
              <Skeleton height={30} width={80} className={minimal ? "" : styles.desktopOnly} />
              {!minimal && (
                <Burger
                  opened={opened}
                  onClick={toggle}
                  className={styles.mobileOnly}
                />
              )}
            </div>
          </Container>
        </header>
      );
    }

    return isLoggedIn ? renderLoggedInHeader() : renderLoggedOutHeader();
  };

  return (
    <>
      {renderAuthSection()}
      {renderMobileDrawer()}
    </>
  );
};
